import { useEffect, useState, useCallback } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { captureError } from "@/lib/sentry";
import { FireIcon } from "@phosphor-icons/react";

export function HotTakeCard({ take, onVote, compact }) {
  const voted = !!take.user_choice;
  const opts = [
    { key: "a", label: take.option_a, pct: take.pct_a },
    { key: "b", label: take.option_b, pct: take.pct_b },
  ];
  return (
    <div data-testid={`hot-take-${take.id}`} className="p-4 rounded-md bg-[#121212] border border-white/10">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#FDB927] mb-2">{take.category}</p>
      <h3 className={`font-display font-bold uppercase tracking-tight mb-3 leading-snug ${compact ? "text-base" : "text-lg"}`}>{take.statement}</h3>
      <div className="grid grid-cols-2 gap-2">
        {opts.map((opt) => {
          const active = take.user_choice === opt.key;
          return (
            <button
              key={opt.key}
              onClick={() => !voted && onVote(take.id, opt.key)}
              disabled={voted}
              data-testid={`vote-${take.id}-${opt.key}`}
              className={`relative overflow-hidden text-left px-3 py-2.5 rounded-md border transition-colors ${
                active ? "border-[#0A84FF]" : "border-white/15 hover:border-white/30"
              } ${voted ? "cursor-default" : ""}`}
            >
              {voted && <div className="absolute inset-y-0 left-0" style={{ width: `${opt.pct}%`, backgroundColor: active ? "rgba(10,132,255,0.22)" : "rgba(255,255,255,0.06)" }} />}
              <div className="relative flex items-center justify-between gap-2">
                <span className="font-display font-bold text-sm uppercase tracking-tight truncate">{opt.label}</span>
                {voted && <span className="font-num text-sm font-black shrink-0">{opt.pct}%</span>}
              </div>
            </button>
          );
        })}
      </div>
      {voted && (
        <p className="text-[10px] text-white/40 mt-2 uppercase tracking-wider" data-testid={`hot-take-result-${take.id}`}>
          {take.total} {take.total === 1 ? "vote" : "votes"} · +10 XP
        </p>
      )}
    </div>
  );
}

export default function HotTakes() {
  const [takes, setTakes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/hot-takes/today")
      .then((r) => setTakes(r.data))
      .catch((err) => captureError("hot takes load failed:", err))
      .finally(() => setLoading(false));
  }, []);

  const vote = useCallback(async (id, choice) => {
    try {
      const r = await api.post(`/hot-takes/${id}/vote`, { choice });
      setTakes((prev) => prev.map((t) => (t.id === id ? r.data : t)));
    } catch (err) {
      captureError("hot take vote failed:", err);
    }
  }, []);

  return (
    <AppLayout>
      <div className="mb-6 fade-up">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FDB927] mb-2 inline-flex items-center gap-1.5">
          <FireIcon weight="fill" /> Hot Take Center
        </p>
        <h1 className="font-display font-bold text-4xl md:text-5xl uppercase tracking-tighter">Today's Debates</h1>
        <p className="text-sm text-white/55 mt-2">Cast your vote, see where the community lands. Fresh takes daily · +10 XP per vote.</p>
      </div>

      {loading ? (
        <div className="text-white/60 text-sm" data-testid="hot-takes-loading">Loading today's debates…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="hot-takes-grid">
          {takes.map((t) => <HotTakeCard key={t.id} take={t} onVote={vote} />)}
          {takes.length === 0 && <div className="text-white/50">No debates today — check back soon.</div>}
        </div>
      )}
    </AppLayout>
  );
}
